import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@tenant/utility/constants/default';
import { checkAllowedAccess } from '@tenant/core/AppPermission/checkAllowedAccess.helper';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import { SYSTEM_ACTOR_USER_ID } from './constants/entityTypes';

// Pure, DOM/React-free helpers for the audit log view (developer-portal-audit-read-ui,
// Portion A). Extracted here — rather than inlined in components — specifically so they are
// unit-testable without a component/DOM harness (this repo has none).

/**
 * Maps FE filter state + paging into the exact query-string params AuditController's
 * [FromQuery] AuditQuery DTO binds (AuditQuery.cs) — PascalCase, matching the DTO's own
 * property names exactly. Every field is optional and omitted entirely when not set.
 * SystemActorOnly takes precedence over ActorUserId server-side
 * (AuditQueryService.BuildFilter) — only one is ever sent so the two controls can never
 * silently conflict on the wire.
 */
export const buildAuditQueryParams = (
  filterState = {},
  page = DEFAULT_PAGE_INDEX,
  pageSize = DEFAULT_PAGE_SIZE,
) => {
  const params = {
    Page: page,
    PageSize: pageSize,
  };

  if (filterState.entityType) params.EntityType = filterState.entityType;
  if (filterState.entityId) params.EntityId = filterState.entityId;

  if (filterState.systemActorOnly) {
    params.SystemActorOnly = true;
  } else if (filterState.actorUserId) {
    params.ActorUserId = filterState.actorUserId;
  }

  if (filterState.occurredFrom) params.OccurredFrom = filterState.occurredFrom;
  if (filterState.occurredTo) params.OccurredTo = filterState.occurredTo;
  if (filterState.correlationId) params.CorrelationId = filterState.correlationId;

  if (
    filterState.appId !== undefined &&
    filterState.appId !== null &&
    filterState.appId !== ''
  ) {
    params.AppId = filterState.appId;
  }

  return params;
};

/**
 * Maps raw AuditLogDocument-shaped Items (AuditQueryResult.Items) into view rows, deriving
 * the display flags the table/chip rendering needs. Null-safe: a null/undefined
 * ActorUserId or AppId must render without error (AC-16), never throw.
 */
export const mapAuditRowsFromResponse = (items = []) => {
  return (items ?? []).map((item) => {
    const isSystemActor = !item?.ActorUserId || item.ActorUserId === SYSTEM_ACTOR_USER_ID;
    const isPlatformGlobal = item?.AppId === null || item?.AppId === undefined;

    return {
      ...item,
      IsSystemActor: isSystemActor,
      IsPlatformGlobal: isPlatformGlobal,
      ActorLabel: isSystemActor ? 'System' : item?.ActorUserName || item?.ActorEmail || 'Unknown',
    };
  });
};

/**
 * The date-window boundary between two different calendars, and the reason it converts by
 * CALENDAR COMPONENTS rather than by instant.
 *
 * The API contract is UTC and says so twice: AuditQuery.OccurredFrom/OccurredTo are documented
 * "Interpreted as UTC" and AuditQueryService.NormalizeUtc stamps an unspecified-Kind bound as UTC
 * before comparing it to the UTC-stored OccurredAt (both bounds inclusive - Gte/Lte). The table
 * column is labelled "Occurred At (UTC)" to match. The pickers, however, run on AdapterDateFns and
 * therefore speak the browser's LOCAL calendar.
 *
 * Converting via the instant (`d.setHours(0,0,0,0)` then `toISOString()`) silently mixes the two:
 * at UTC+7 a picked 3 Sep becomes 2026-09-02T17:00:00Z, so a field labelled UTC straddles two UTC
 * days and disagrees with the column beside it. Reading the LOCAL y/m/d and re-stamping it as a UTC
 * instant keeps the picked calendar day meaning exactly that day in UTC, in every timezone.
 */
const toValidDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Local calendar day -> that day's inclusive UTC lower bound, as an ISO string. */
export const utcDayStartIsoFromLocalDate = (value) => {
  const d = toValidDate(value);
  if (!d) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)).toISOString();
};

/** Local calendar day -> that day's inclusive UTC upper bound, as an ISO string. Widening to the
 *  end of the day is what makes a picked range cover the whole of the days chosen: a bare lower
 *  bound would resolve to 00:00 and exclude everything that happened on the chosen end date. */
export const utcDayEndIsoFromLocalDate = (value) => {
  const d = toValidDate(value);
  if (!d) return null;
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999),
  ).toISOString();
};

/**
 * The inverse, for painting a stored bound back into a picker. Also component-wise: handing the
 * raw instant to the picker would show the PREVIOUS day at every negative UTC offset (2026-09-03T00:00Z
 * is 2 Sep 19:00 in UTC-5), so the field would contradict the value it holds.
 */
export const localDateFromUtcDayIso = (iso) => {
  const d = toValidDate(iso);
  if (!d) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Mirrors AuditQueryService.MaxDateRangeSpan (BE/MarketplaceDeveloper.WebAPI/Services/AuditQueryService.cs).
const MAX_DATE_RANGE_SPAN_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Computes the {occurredFrom, occurredTo} window for "Load earlier records" (AC-13) — pages
 * the date window backward by the same span as the current window (or the server's default
 * 90-day span-cap when no explicit window was applied), anchored at the oldest record
 * currently shown so the next fetch doesn't re-request rows already on screen. No cursor —
 * mirrors the Wave 1 WindowClamped date-window-clamp model (AuditQueryService.ResolveDateRange).
 */
export const computeLoadEarlierWindow = (currentFrom, currentTo, oldestShownOccurredAt) => {
  const anchorMs = oldestShownOccurredAt
    ? new Date(oldestShownOccurredAt).getTime()
    : currentFrom
      ? new Date(currentFrom).getTime()
      : Date.now();

  const spanMs =
    currentFrom && currentTo
      ? new Date(currentTo).getTime() - new Date(currentFrom).getTime()
      : MAX_DATE_RANGE_SPAN_MS;

  const effectiveSpanMs = spanMs > 0 ? spanMs : MAX_DATE_RANGE_SPAN_MS;

  return {
    occurredFrom: new Date(anchorMs - effectiveSpanMs).toISOString(),
    occurredTo: new Date(anchorMs).toISOString(),
  };
};

/**
 * Portion A: the AppId filter is free/global for any internal AUDIT_READ holder. Kept as a
 * standalone predicate (rather than inlining a useAllowedAccess call) so Portion B can later
 * swap in an owner-scope check without restructuring the filter/table components, and so
 * it's unit-testable without a component harness.
 */
export const shouldShowAppIdFilter = (permissions = []) =>
  checkAllowedAccess(permissions, [AuthorizePermission.AUDIT_READ]);
